import { forwardRef, useState } from 'react';
import { Button, Tooltip } from '~/components/granular';
import { classNames } from '~/utils/styles';

import type { ToggleGroupItem, ToggleGroupProps } from './ToggleGroup.types';

function getValue(item: string | ToggleGroupItem | undefined) {
  return typeof item === 'object' ? item.value : item;
}

function getLabel(item: string | ToggleGroupItem) {
  return typeof item === 'object' ? item.label : item;
}

export const ToggleGroup = forwardRef<HTMLFieldSetElement, ToggleGroupProps>(
  ({ className, disabled, items, size = 'large', value, onValueChange, ...props }, forwardedRef) => {
    const [activeIndex, setActiveIndex] = useState<number>(() =>
      items.findIndex((item) => getValue(item) === getValue(value)),
    );
    const [prevProps, setPrevProps] = useState({ items, value });
    if (prevProps.value !== value || prevProps.items !== items) {
      setPrevProps({ items, value });
      setActiveIndex(items.findIndex((item) => getValue(item) === getValue(value)));
    }

    const classes = classNames('flex', className);

    return (
      <fieldset className={classes} ref={forwardedRef} {...props}>
        {items.map((item, index) => {
          const isDisabled = (typeof item === 'object' && item.disabled) || disabled;

          // Segments overlap their neighbour's border by 1px, so the hovered one is raised to keep
          // its border unclipped — enabled only, or a disabled segment paints over its neighbour.
          const isActive = index === activeIndex;

          // A disabled group still shows its value: the selected segment keeps a lighter teal fill rather than
          // turning grey like the rest.
          const btnClasses = classNames('relative -mr-px justify-center! text-neutral-800 outline-hidden!', {
            'bg-teal-200! text-teal-700!': isActive && !isDisabled,
            'border border-solid! border-neutral-300! bg-teal-100! text-teal-700!': isActive && isDisabled,
            'border border-solid! border-neutral-300 bg-neutral-200! text-neutral-500! hover:border-neutral-300':
              isDisabled && !isActive,
            'focus:border-neutral-300! focus:bg-teal-100! focus:text-teal-700! active:bg-teal-100! active:text-teal-700!':
              !isDisabled,
            'hover:bg-neutral-100! hover:text-neutral-800!': index !== activeIndex && !isDisabled,
            'hover:z-10 hover:border-teal-700': !isDisabled,
          });

          const onClick = () => {
            onValueChange?.(items[index]);
            setActiveIndex(index);
          };

          const button = (
            <Button
              appearance="secondary"
              autoFocus={index === activeIndex}
              className={btnClasses}
              disabled={isDisabled}
              key={getValue(item)}
              onClick={onClick}
              size={size}
            >
              {getLabel(item)}
            </Button>
          );

          const disabledTooltipText = typeof item === 'object' && isDisabled ? item.disabledTooltipText : undefined;

          if (!disabledTooltipText) {
            return button;
          }

          return (
            <Tooltip key={getValue(item)} trigger={button} triggerDisabled>
              {disabledTooltipText}
            </Tooltip>
          );
        })}
      </fieldset>
    );
  },
);
